import React, { useRef, useState } from "react";
import { FullscreenTakeover, Step, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { withClientContext } from "~/apollo/client";
import { useGetEntityWithTransactionCountsLazyQuery } from "~/graphql/mainHooks";
import {
  type GetEntityPreviewGlSyncQueryHookResult,
  type GetEntitiesSyncStatusQueryResult,
  useCheckTransactionsWithInvalidPositionIdsLazyQuery,
  useGetEntityPreviewGlSyncLazyQuery,
  useStartEntityGlSyncMutation,
} from "~/graphql/supergraphHooks";
import { TransactionCategoryType } from "~/graphql/supergraphTypes";
import { generateDataMapsUrl } from "~/pages/FundAccounting/utils/helpers";
import { DATE_FORMATS } from "~/utils/constants";
import { toStandardFormat } from "~/utils/datetime";
import { getErrorMessage } from "~/utils/errors";
import { toFederationGlobalId, toGlobalId } from "~/utils/graphql-helpers";
import { getCapitalActivityLabel, TransactionCategory } from "../../../types";
import StepNavigationIndicator from "../StepNavigation";
import { SYNC_SETTINGS_TYPES } from "./constants";
import { getEndDate } from "./helpers";
import EmptyState from "./States/EmptyState";
import ErrorState from "./States/ErrorState";
import LoadingState from "./States/LoadingState";
import UnmappedCategoriesState from "./States/UnmappedCategoriesState";
import styles from "./styles";
import SyncSummary from "./SyncSummary";
import { InitialValues } from "./types";
import UpdateDateRange from "./UpdateDateRange";

type EntityGlSyncStatusResponse = NonNullable<
  GetEntitiesSyncStatusQueryResult["data"]
>["entitiesGLSyncStatus"][0];

type Props = {
  onExit: () => void;
  arenaName: string;
  entityName: string | undefined;
  arenaSubdomain: string;
  entityId?: number;
  arenaId: number;
  userId?: number;
  defaultSyncTransactionCategory?: string;
  syncStatuses?: EntityGlSyncStatusResponse;
  onSyncTransfer: () => void;
  // Invoked after a successful sync so the parent can return the user to the
  // entities list and restore scroll position.
  onSyncStarted?: () => void;
};

enum STEP_NAMES {
  updateDateRange = "Configure sync settings",
  reviewInitialImport = "Review and sync",
}

type StepRendererType = {
  [key in STEP_NAMES.updateDateRange | STEP_NAMES.reviewInitialImport]: () => React.ReactNode;
};

export const UpdateTransactionTakeover = ({
  onExit,
  arenaName,
  entityName,
  arenaSubdomain,
  arenaId,
  entityId,
  userId,
  defaultSyncTransactionCategory,
  syncStatuses,
  onSyncTransfer,
  onSyncStarted,
}: Props) => {
  const showCapitalActivityLabelCanopy = useFeatureFlag(
    "showCapitalActivityLabelCanopy",
    false,
  );
  const capitalActivityLabel = getCapitalActivityLabel(showCapitalActivityLabelCanopy);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [entitySyncLoading, setEntitySyncLoading] = useState<boolean>(false);
  const [entitySyncData, setEntitySyncData] = useState<
    GetEntityPreviewGlSyncQueryHookResult["data"] | null
  >(null);
  const [existingTransactionCategories, setExistingTransactionCategories] = useState<
    TransactionCategory[]
  >([]);
  const [nullJsqPositionIds, setNullJsqPositionIds] = useState<string[]>([]);
  const [invalidJsqPositionIds, setInvalidJsqPositionIds] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<STEP_NAMES>(STEP_NAMES.updateDateRange);

  const defaultErrorMessage = entityName
    ? `Please go back and review your selected date range for ${entityName} or exit.`
    : `Please go back and review your selected date range or exit.`;
  const endDateRef = useRef<string>("");
  const startDateRef = useRef<string | null>(null);
  const includeAllTransactionsRef = useRef<boolean>(false);
  const includeCapitalCallRef = useRef<boolean>(false);
  const includeCapitalTransactionRef = useRef<boolean>(false);
  const includeDistributionRef = useRef<boolean>(false);
  const includeNetIncomeRef = useRef<boolean>(false);
  const [requestedTransactionCategories, setRequestedTransactionCategories] = useState<
    Set<string>
  >(new Set());

  const addRequestedTransactionCategories = (categoryName: string) => {
    setRequestedTransactionCategories((prevSet) => new Set(prevSet).add(categoryName));
  };

  const handleOnExit = () => {
    setIsSyncing(false);
    setEntitySyncLoading(false);
    setEntitySyncData(null);
    setNullJsqPositionIds([]);
    setInvalidJsqPositionIds([]);
    setExistingTransactionCategories([]);
    setRequestedTransactionCategories(new Set()); // reset this data structure on navigating back.
    setCurrentStep(STEP_NAMES.updateDateRange);
    onExit();
  };
  const onError = () => {
    setErrorMessage(defaultErrorMessage);
  };
  const goBack = () => {
    setIsSyncing(false);
    setEntitySyncLoading(false);
    setEntitySyncData(null);
    setErrorMessage("");
    setNullJsqPositionIds([]);
    setInvalidJsqPositionIds([]);
    setExistingTransactionCategories([]);
    setRequestedTransactionCategories(new Set()); // reset this data structure on navigating back.
    setCurrentStep(STEP_NAMES.updateDateRange);
  };

  const [fetchQuery] = useGetEntityPreviewGlSyncLazyQuery({
    fetchPolicy: "network-only",
    onError,
  });

  const [fetchCheckTransactions] = useCheckTransactionsWithInvalidPositionIdsLazyQuery({
    fetchPolicy: "network-only",
    onError,
  });

  const [fetchEntityWithTransactionCounts] = useGetEntityWithTransactionCountsLazyQuery({
    context: withClientContext("staff", {
      arenaId: arenaId,
    }),
    fetchPolicy: "network-only",
    onError,
  });

  const [startSyncMutate] = useStartEntityGlSyncMutation({
    fetchPolicy: "network-only",
    onError: (error: unknown) => {
      setErrorMessage(getErrorMessage(error));
    },
  });

  const handleSubmit = async (value: InitialValues) => {
    setEntitySyncLoading(true);
    const endDate = getEndDate(value);
    endDateRef.current = endDate || "";

    const startDate =
      value.syncSettings === SYNC_SETTINGS_TYPES.allTransactions && value.integrationStart
        ? toStandardFormat(new Date(value.integrationStart), DATE_FORMATS["YYYY-MM-DD"].long)
        : null;
    startDateRef.current = startDate;

    const includeAllTransactions =
      value.syncSettings === SYNC_SETTINGS_TYPES.allTransactions && value.integrationThrough
        ? true
        : false;
    includeAllTransactionsRef.current = includeAllTransactions;

    const globalArenaId = toFederationGlobalId(
      "ArenaObjectType",
      arenaId ? `${arenaId}` : "-1",
    );

    includeCapitalCallRef.current = value.capitalCalls;
    includeCapitalTransactionRef.current = value.capitalActivity;
    includeDistributionRef.current = value.distributions;
    includeNetIncomeRef.current = value.netIncome;

    if (value.capitalCalls) {
      addRequestedTransactionCategories("Capital call");
    }
    if (value.capitalActivity) {
      addRequestedTransactionCategories(capitalActivityLabel);
    }
    if (value.distributions) {
      addRequestedTransactionCategories("Distribution");
    }
    if (value.netIncome) {
      addRequestedTransactionCategories("Net income");
    }

    const categoriesToCheck: Array<{ transactionCategory: TransactionCategoryType }> = [
      { transactionCategory: "capital_call" as const, enabled: value.capitalCalls },
      { transactionCategory: "capital_transaction" as const, enabled: value.capitalActivity },
      { transactionCategory: "distribution" as const, enabled: value.distributions },
      { transactionCategory: "net_income" as const, enabled: value.netIncome },
    ]
      .filter(({ enabled }) => enabled)
      .map(({ transactionCategory }) => ({ transactionCategory }));

    const [response, checkResponses] = await Promise.all([
      fetchQuery({
        variables: {
          input: {
            arenaId: globalArenaId,
            entityId: entityId || -1,
            startDate: startDate,
            endDate: endDate ?? "",
            includeAllTransactions: includeAllTransactions,
          },
          withCapitalCall: value.capitalCalls,
          withCapitalTransaction: value.capitalActivity,
          withDistribution: value.distributions,
          withNetIncome: value.netIncome,
        },
      }),
      Promise.all(
        categoriesToCheck.map(({ transactionCategory }) =>
          fetchCheckTransactions({
            variables: {
              input: {
                arenaId: globalArenaId,
                entityId: entityId || -1,
                isTransfer: false,
                startDate: startDate,
                endDate: endDate ?? "",
                transactionCategory,
              },
            },
          }),
        ),
      ),
    ]);

    const result = response.data?.entityPreviewGLSyncNew;
    // TODO: Need to update GraphQL queries in main to support filtering
    // all transaction types by date, only net income is currently supported.
    // Alternatively we could query this via REST on the backend.
    // NOTE: previously commented out due to permission issue https://junipersquare.atlassian.net/browse/ENG-28777
    if (result?.netIncome) {
      if (result.netIncome.newTransactions.length > 0) {
        const newStartDate = result.netIncome.newTransactions[0].startDate;

        const entityResponse = await fetchEntityWithTransactionCounts({
          variables: {
            entityId: toGlobalId("AccountObjectType", entityId || -1),
            startDate: newStartDate,
            withCapitalCall: value.capitalCalls,
            withDistribution: value.distributions,
            withRedemption: value.capitalActivity,
            withNetIncome: value.netIncome,
          },
        });

        // sum net income counts in response
        const netIncomeCount =
          entityResponse.data?.investmentEntity?.owningPositions?.edges.reduce(
            (acc, edge) => acc + (edge.node.netIncomes?.count || 0),
            0,
          ) || 0;
        const capitalCallCount =
          entityResponse.data?.investmentEntity?.owningPositions?.edges.reduce(
            (acc, edge) => acc + (edge.node.capitalCalls?.count || 0),
            0,
          ) || 0;
        const distributionCount =
          entityResponse.data?.investmentEntity?.owningPositions?.edges.reduce(
            (acc, edge) => acc + (edge.node.distributions?.count || 0),
            0,
          ) || 0;
        const redemptionCount =
          entityResponse.data?.investmentEntity?.owningPositions?.edges.reduce(
            (acc, edge) => acc + (edge.node.redemptions?.count || 0),
            0,
          ) || 0;

        const transactionCategories = [];
        if (capitalCallCount > 0) {
          transactionCategories.push(TransactionCategory.CapitalCalls);
        }
        if (distributionCount > 0) {
          transactionCategories.push(TransactionCategory.Distributions);
        }
        if (redemptionCount > 0) {
          transactionCategories.push(TransactionCategory.Redemptions);
        }
        if (netIncomeCount > 0) {
          transactionCategories.push(TransactionCategory.NetIncome);
        }
        setExistingTransactionCategories(transactionCategories);
      }
    }

    const allNullIds = new Set<string>();
    const allInvalidIds = new Set<string>();
    for (const checkResponse of checkResponses) {
      const result = checkResponse.data?.checkTransactionsWithInvalidPositionIds;
      result?.nullJsqPositionIds?.forEach((id: string) => allNullIds.add(id));
      result?.invalidJsqPositionIds?.forEach((id: string) => allInvalidIds.add(id));
    }
    setNullJsqPositionIds([...allNullIds]);
    setInvalidJsqPositionIds([...allInvalidIds]);

    setEntitySyncData(response.data);
    setEntitySyncLoading(false);
    setCurrentStep(STEP_NAMES.reviewInitialImport);
  };

  const handleStartSync = async (invalidBatchIds: number[]) => {
    setIsSyncing(true);

    const input = {
      arenaId: arenaId || -1,
      userId: userId || -1,
      entityId: entityId || -1,
      startDate: startDateRef.current,
      endDate: endDateRef.current,
      includeAllTransactions: includeAllTransactionsRef.current,
      includeCapitalCall: includeCapitalCallRef.current,
      includeCapitalTransaction: includeCapitalTransactionRef.current,
      includeDistribution: includeDistributionRef.current,
      includeNetIncome: includeNetIncomeRef.current,
      // excludeGlBatchIds is out of date, as backend is using sync settings start date and the passed end date to determine which batches to exclude
      excludeGlBatchIds: invalidBatchIds,
    };

    // `.catch` is a safety net; the mutation's onError handler already sets the
    // error message.
    const response = await startSyncMutate({ variables: { input } }).catch(() => null);

    if (response?.data?.startEntityGLSync?.success) {
      toast.success(entityName ? `Sync started for ${entityName}.` : "Sync started.");
      (onSyncStarted ?? handleOnExit)();
      return;
    }
    // The sync did not start. onError sets a specific message for GraphQL/network
    // errors; fall back to a helpful default (e.g. a non-error `success: false`
    // response) so the ErrorState always renders with actionable copy.
    setErrorMessage(
      (current) =>
        current ||
        `We couldn't start the sync${
          entityName ? ` for ${entityName}` : ""
        }. Please go back and try again, or contact support if the problem persists.`,
    );
    setIsSyncing(false);
  };

  const onChangeSettings = () => {
    setErrorMessage("");
    setEntitySyncLoading(false);
    setEntitySyncData(null);
    setNullJsqPositionIds([]);
    setInvalidJsqPositionIds([]);
    setExistingTransactionCategories([]);
    setRequestedTransactionCategories(new Set()); // reset this data structure on navigating back.
    setCurrentStep(STEP_NAMES.updateDateRange);
  };

  // step to component mapping
  const stepsRenders: StepRendererType = {
    [STEP_NAMES.updateDateRange]: () => (
      <UpdateDateRange
        entityId={entityId}
        arenaId={arenaId}
        arenaName={arenaName}
        arenaSubdomain={arenaSubdomain}
        entityName={entityName}
        onSubmit={handleSubmit}
        onSyncTransfer={() => {
          onSyncTransfer();
        }}
        defaultSyncTransactionCategory={defaultSyncTransactionCategory}
        syncStatuses={syncStatuses}
      />
    ),
    [STEP_NAMES.reviewInitialImport]: () => (
      <SyncSummary
        syncFromTransferHistory={includeAllTransactionsRef.current}
        arenaName={arenaName}
        entityName={entityName}
        data={entitySyncData}
        arenaSubdomain={arenaSubdomain}
        existingTransactionCategories={existingTransactionCategories}
        onStartSync={handleStartSync}
        onBack={onChangeSettings}
        requestedTransactionCategories={requestedTransactionCategories}
        endDate={endDateRef.current}
        entityId={entityId || -1}
        arenaId={arenaId || -1}
        nullJsqPositionIds={nullJsqPositionIds}
        invalidJsqPositionIds={invalidJsqPositionIds}
      />
    ),
  };

  // steps
  const getReviewStepStatus = () => {
    if (isSyncing) {
      return "complete";
    }
    return currentStep == STEP_NAMES.reviewInitialImport ? "active" : "inactive";
  };
  const steps: Step[] = [
    {
      name: STEP_NAMES.updateDateRange,
      // url is used as key in the .map loop for steps in StepIndicator
      // it is required for React
      url: STEP_NAMES.updateDateRange,
      status: currentStep == STEP_NAMES.updateDateRange ? "active" : "complete",
      hidden: false,
      index: 1,
    },
    {
      name: STEP_NAMES.reviewInitialImport,
      url: STEP_NAMES.reviewInitialImport,
      status: getReviewStepStatus(),
      hidden: false,
      index: 2,
    },
  ];

  const previewResult = entitySyncData?.entityPreviewGLSyncNew;
  const selectedCategoryPreviews = [
    { category: TransactionCategory.CapitalCalls, preview: previewResult?.capitalCall },
    { category: TransactionCategory.Redemptions, preview: previewResult?.capitalTransaction },
    { category: TransactionCategory.Distributions, preview: previewResult?.distribution },
    { category: TransactionCategory.NetIncome, preview: previewResult?.netIncome },
  ].filter(({ preview }) => !!preview);

  const hasTransactions = selectedCategoryPreviews.some(
    ({ preview }) =>
      (preview?.newTransactions?.length || 0) > 0 ||
      (preview?.existingTransactions?.length || 0) > 0,
  );

  const unmappedSelectedCategories = selectedCategoryPreviews
    .filter(({ preview }) => preview?.hasMappedTransactionTypes === false)
    .map(({ category }) => category);
  const allSelectedCategoriesUnmapped =
    selectedCategoryPreviews.length > 0 &&
    unmappedSelectedCategories.length === selectedCategoryPreviews.length;

  const dataMapsUrl = generateDataMapsUrl(arenaSubdomain);

  const renderEmptyState = () => {
    if (allSelectedCategoriesUnmapped) {
      return (
        <UnmappedCategoriesState
          unmappedCategories={unmappedSelectedCategories}
          mappingName={previewResult?.mappingName}
          entityName={entityName}
          dataMapsUrl={dataMapsUrl}
          onBackToSettings={onChangeSettings}
        />
      );
    }
    return <EmptyState onExit={handleOnExit} onChangeSettings={onChangeSettings} />;
  };

  const title = entityName ? `Sync JSQ transactions · ${entityName}` : "Sync JSQ transactions";

  const content = (
    <>
      {(entitySyncLoading || isSyncing) && <LoadingState />}
      {!entitySyncLoading && !isSyncing && !!errorMessage && (
        <ErrorState
          onExit={handleOnExit}
          goBack={goBack}
          entityName={entityName}
          errorMessage={errorMessage}
        />
      )}
      {!entitySyncLoading &&
        !isSyncing &&
        !errorMessage &&
        entitySyncData &&
        !hasTransactions &&
        renderEmptyState()}
      {!entitySyncLoading &&
        !isSyncing &&
        !errorMessage &&
        (!entitySyncData || hasTransactions) &&
        stepsRenders[currentStep]()}
    </>
  );

  return (
    // style here make the takeover full screen
    <FullscreenTakeover css={styles.fullScreenTakeover}>
      <FullscreenTakeover.Header onExit={handleOnExit} title={title} />
      <FullscreenTakeover.Subheader>
        <StepNavigationIndicator steps={steps} />
      </FullscreenTakeover.Subheader>
      <FullscreenTakeover.Body>{content}</FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
